import Button from '@mui/material/Button'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import {
	type LightningReportScheduleDayOfWeek,
	type LightningReportScheduleFrequency,
	lightningReportScheduleDaysOfWeek,
	lightningReportScheduleFrequencies,
} from '@xweather/protect-types'
import i18next from 'i18next'
import { useEffect, useEffectEvent, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Box } from '../../../../../components/Box.tsx'
import {
	Alert,
	Divider,
	FormControlLabel,
	FormLabel,
	InputAdornment,
	Menu,
	MenuItem,
	MuiIconButton,
	Popover,
	Switch,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '../../../../../components/generic.tsx'
import { I18n } from '../../../../../components/I18n.tsx'
import { Icon } from '../../../../../components/Icon.tsx'
import { Stack } from '../../../../../components/Stack.tsx'
import { useMenu } from '../../../../../components/useMenu.ts'
import { useLightningReportDataAvailability } from '../../../../../data/reports/lightning.ts'
import { type Dayjs, dayjs } from '../../../../../dayjs.ts'
import { ids } from '../../../../../ids.ts'
import { useDateFormat, useFormatDate } from '../../../../preferences/useFormatDate.tsx'
import { DateTimeField } from '../../fields/DateTimeField.tsx'
import { StartEndDateTimeFields } from '../../fields/StartEndDateTimeFields.tsx'
import { addPeriod, currentPeriodStart, periodInstances } from '../scheduleUtils.ts'
import {
	type FaultTime,
	type LightningReportForm,
	oneOffTimingToTimeRange,
	type RepeatedTiming,
	useLightningFormValues,
} from '../schema.tsx'

const hasValidTimeSet = (timing: LightningReportForm['timing']) => {
	if (timing.type === 'ONE_OFF') {
		const { time } = timing
		switch (time.type) {
			case 'start-end':
				return Boolean(time.start && time.end)
			case 'fault-time':
				return Boolean(time.faultTime)
			default:
				time satisfies never
		}
	}

	return false
}

export const LightningTimeSelection = () => {
	const form = useFormContext<LightningReportForm>()
	const [timing] = useLightningFormValues(['timing'])
	const validateTimingIfSet = useEffectEvent(() => {
		if (timing.type === 'REPEATED') {
			void form.trigger('timing.frequency')
		} else if (hasValidTimeSet(timing)) {
			void form.trigger('timing.time')
		}
	})

	// Validate timing when entering this step, because the user might have updated the geometry
	// in the previous step in a way that affects what time ranges / frequencies are allowed.
	useEffect(validateTimingIfSet, [])

	return (
		<>
			<TimeRangeSection />
			{timing.type === 'ONE_OFF' && <DataAvailabilityAlert />}
		</>
	)
}

const TimeRangeSection = () => {
	const { setValue } = useFormContext<LightningReportForm>()
	const [timing, timezone] = useLightningFormValues(['timing', 'preferences.timezone'])
	const isFaultTimeEntryInputMode = timing.type === 'ONE_OFF' && timing.time.type === 'fault-time'

	const onFaultTimeToggled = (newIsFaultTimeEntryInputMode: boolean) => {
		setValue('timing', {
			type: 'ONE_OFF',
			time: newIsFaultTimeEntryInputMode
				? { type: 'fault-time', faultTime: '', buffer: { unit: 'second', amount: 60 } }
				: { type: 'start-end', start: '', end: '' },
		})
	}

	const onTimingModeToggled = (newMode: LightningReportForm['timing']['type']) => {
		if (newMode === 'REPEATED') {
			setValue('timing', {
				type: 'REPEATED',
				frequency: 'DAILY',
				dayOfWeek: undefined,
				startScheduleAt: currentPeriodStart('DAILY', timezone, undefined).toISOString(),
				stopScheduleAt: undefined,
			})
		} else {
			setValue('timing', { type: 'ONE_OFF', time: { type: 'start-end', start: '', end: '' } })
		}
	}

	return (
		<>
			<FormControlLabel
				control={
					<Switch
						data-ta={ids.reports.form.lightning.timeRange.faultTimeEntry.toggle}
						checked={isFaultTimeEntryInputMode}
						onChange={(_, value) => onFaultTimeToggled(value)}
					/>
				}
				label={<I18n i18nKey={($) => $.reports.lightning.form.faultTimeEntry} />}
			/>
			<Divider />
			{!isFaultTimeEntryInputMode && <ScheduleAndPeriodFields onTimingModeToggled={onTimingModeToggled} />}
			{isFaultTimeEntryInputMode ? (
				<FaultTimeTimeRangeSection />
			) : timing.type === 'REPEATED' ? (
				<RepeatedTimeRangeSection timing={timing} />
			) : (
				<StartEndTimeRangeSection />
			)}
		</>
	)
}

const ScheduleAndPeriodFields = ({
	onTimingModeToggled,
}: {
	onTimingModeToggled: (mode: LightningReportForm['timing']['type']) => void
}) => {
	const { setValue } = useFormContext<LightningReportForm>()
	const [timing, timezone] = useLightningFormValues(['timing', 'preferences.timezone'])

	const onFrequencyChanged = (newFrequency: LightningReportScheduleFrequency) => {
		if (timing.type !== 'REPEATED' || newFrequency === timing.frequency) return
		const dayOfWeek = newFrequency === 'WEEKLY' ? (timing.dayOfWeek ?? 'MON') : undefined
		setValue(
			'timing',
			{
				type: 'REPEATED',
				frequency: newFrequency,
				dayOfWeek,
				startScheduleAt: currentPeriodStart(newFrequency, timezone, dayOfWeek).toISOString(),
				stopScheduleAt: undefined,
			},
			{ shouldValidate: true },
		)
	}

	return (
		<Box display='grid' gridTemplateColumns={timing.type === 'REPEATED' ? '1fr 1fr' : '1fr'} gap={2}>
			<Box>
				<FormLabel>
					<I18n i18nKey={($) => $.reports.lightning.form.repetition.field} />
				</FormLabel>
				<TextField
					data-ta={ids.reports.form.lightning.timeRange.repeatedSchedule.repetitionSelect}
					select
					fullWidth
					size='small'
					value={timing.type}
				>
					{(['ONE_OFF', 'REPEATED'] as const satisfies LightningReportForm['timing']['type'][]).map((option) => (
						<MenuItem
							data-ta={ids.reports.form.lightning.timeRange.repeatedSchedule.repetitionOption(option)}
							key={option}
							value={option}
							onClick={() => onTimingModeToggled(option)}
						>
							<I18n i18nKey={($) => $.reports.lightning.form.repetition.options[option]} />
						</MenuItem>
					))}
				</TextField>
			</Box>
			{timing.type === 'REPEATED' && (
				<Box>
					<FormLabel>
						<I18n i18nKey={($) => $.reports.lightning.form.repeatedSchedule.period} />
					</FormLabel>
					<ToggleButtonGroup exclusive fullWidth size='small' value={timing.frequency}>
						{lightningReportScheduleFrequencies.map((frequency) => (
							<ToggleButton
								data-ta={ids.reports.form.lightning.timeRange.repeatedSchedule.period(frequency)}
								key={frequency}
								value={frequency}
								selected={timing.frequency === frequency}
								onClick={() => onFrequencyChanged(frequency)}
							>
								<I18n i18nKey={($) => $.reports.lightning.common.frequencies[frequency]} />
							</ToggleButton>
						))}
					</ToggleButtonGroup>
				</Box>
			)}
		</Box>
	)
}

const RepeatedTimeRangeSection = ({ timing }: { timing: RepeatedTiming }) => {
	const { setValue } = useFormContext<LightningReportForm>()
	const [timezone] = useLightningFormValues(['preferences.timezone'])
	const { frequency, dayOfWeek, startScheduleAt, stopScheduleAt } = timing

	// When the timezone changes, the previously chosen "startScheduleAt" and "stopScheduleAt" may end up before the new
	// "today" boundary. In this case, reset Reset to today's period start (and clear the end date if it falls before
	// the new minimum), since the user can't see the dates shift but the API would reject them.
	const resetInvalidScheduleDates = useEffectEvent(() => {
		const periodStart = currentPeriodStart(frequency, timezone, dayOfWeek)
		const newStart = dayjs.utc(startScheduleAt).isBefore(periodStart) ? periodStart.toISOString() : startScheduleAt
		const minStop = addPeriod(dayjs.utc(newStart), frequency, 1)
		const newStop = stopScheduleAt && !dayjs.utc(stopScheduleAt).isBefore(minStop) ? stopScheduleAt : undefined
		if (newStart !== startScheduleAt || newStop !== stopScheduleAt) {
			setValue('timing', { ...timing, startScheduleAt: newStart, stopScheduleAt: newStop }, { shouldValidate: true })
		}
	})
	// biome-ignore lint/correctness/useExhaustiveDependencies: The logic needs to run when `timezone` changes
	useEffect(resetInvalidScheduleDates, [timezone])

	const onWeekdayChanged = (newWeekday: LightningReportScheduleDayOfWeek) => {
		setValue(
			'timing',
			{
				...timing,
				dayOfWeek: newWeekday,
				startScheduleAt: currentPeriodStart('WEEKLY', timezone, newWeekday).toISOString(),
				stopScheduleAt: undefined,
			},
			{ shouldValidate: true },
		)
	}

	const onStartChanged = (newStart: string) => {
		const keepStop = stopScheduleAt && dayjs(stopScheduleAt).isAfter(newStart)
		setValue(
			'timing',
			{ ...timing, startScheduleAt: newStart, stopScheduleAt: keepStop ? stopScheduleAt : undefined },
			{ shouldValidate: true },
		)
	}

	const onStopChanged = (newStop: string | undefined) => {
		setValue('timing', { ...timing, stopScheduleAt: newStop }, { shouldValidate: true })
	}

	return (
		<>
			{frequency === 'WEEKLY' && <StartingWeekdayField value={dayOfWeek} onChange={onWeekdayChanged} />}
			<Box flex col gap={0.5}>
				<Box display='grid' gridTemplateColumns='repeat(auto-fit, minmax(240px, 1fr))' gap={2}>
					<Box>
						<FormLabel>
							<I18n i18nKey={($) => $.reports.lightning.form.repeatedSchedule.startDateLabel[frequency]} />
						</FormLabel>
						<RepeatStartDateField
							frequency={frequency}
							dayOfWeek={dayOfWeek}
							timezone={timezone}
							value={startScheduleAt}
							onChange={onStartChanged}
						/>
					</Box>
					<Box>
						<FormLabel>
							<I18n i18nKey={($) => $.reports.lightning.form.repeatedSchedule.endDateLabel} />
						</FormLabel>
						<RepeatEndDateField
							frequency={frequency}
							timezone={timezone}
							startScheduleAt={startScheduleAt}
							value={stopScheduleAt}
							onChange={onStopChanged}
						/>
					</Box>
				</Box>
				<Typography color='textSecondary' variant='body2'>
					<I18n i18nKey={($) => $.reports.lightning.form.repeatedSchedule.generatedAfterPeriodEnds} />
				</Typography>
			</Box>

			<Controller
				name='timing.frequency'
				render={({ fieldState }) =>
					fieldState.error?.message ? (
						<Alert severity='error'>{fieldState.error.message}</Alert>
					) : (
						<RepeatedScheduleInfoAlert frequency={frequency} startScheduleAt={startScheduleAt} />
					)
				}
			/>
		</>
	)
}

const StartingWeekdayField = ({
	value,
	onChange,
}: {
	value: LightningReportScheduleDayOfWeek | undefined
	onChange: (next: LightningReportScheduleDayOfWeek) => void
}) => (
	<Box>
		<FormLabel required>
			<I18n i18nKey={($) => $.reports.lightning.form.repeatedSchedule.startingWeekday} />
		</FormLabel>
		<ToggleButtonGroup exclusive fullWidth size='small' value={value}>
			{lightningReportScheduleDaysOfWeek.map((weekday) => (
				<ToggleButton
					data-ta={ids.reports.form.lightning.timeRange.repeatedSchedule.weekday(weekday)}
					key={weekday}
					value={weekday}
					selected={value === weekday}
					onClick={() => onChange(weekday)}
				>
					<I18n i18nKey={($) => $.reports.lightning.form.repeatedSchedule.weekdays[weekday]} />
				</ToggleButton>
			))}
		</ToggleButtonGroup>
	</Box>
)

const RepeatStartDateField = ({
	frequency,
	dayOfWeek,
	timezone,
	value,
	onChange,
}: {
	frequency: LightningReportScheduleFrequency
	dayOfWeek: LightningReportScheduleDayOfWeek | undefined
	timezone: string
	value: string
	onChange: (next: string) => void
}) => {
	const dateFormat = useDateFormat()
	const formatDate = useFormatDate()
	// Schedule dates are stored as UTC midnight (timezone-independent in the UI), so we display them
	// using `dayjs.utc(...)` rather than converting through the user's timezone.
	if (frequency === 'DAILY') {
		return (
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<DatePicker
					value={value ? dayjs.utc(value) : null}
					timezone='UTC'
					minDate={currentPeriodStart('DAILY', timezone, undefined)}
					slotProps={{
						textField: {
							size: 'small',
							fullWidth: true,
							// @ts-expect-error: missing data-ta prop in their types
							'data-ta': ids.reports.form.lightning.timeRange.repeatedSchedule.startScheduleAt,
						},
					}}
					onChange={(newValue) => {
						if (newValue?.isValid()) {
							onChange(newValue.startOf('day').toISOString())
						}
					}}
					format={dateFormat('DD.MM.YYYY.HH.mm')}
				/>
			</LocalizationProvider>
		)
	}

	const PERIODS_TO_SHOW = 50
	const options = periodInstances(currentPeriodStart(frequency, timezone, dayOfWeek), frequency, PERIODS_TO_SHOW)
	const containsValue = options.some((option) => option.toISOString() === value)
	return (
		<TextField
			data-ta={ids.reports.form.lightning.timeRange.repeatedSchedule.startScheduleAt}
			select
			fullWidth
			size='small'
			value={containsValue ? value : (options[0]?.toISOString() ?? '')}
		>
			{options.map((option) => {
				const iso = option.toISOString()
				return (
					<MenuItem key={iso} value={iso} onClick={() => onChange(iso)}>
						{formatDate(option, 'DD.MM.YYYY.HH.mm', 'UTC')}
					</MenuItem>
				)
			})}
		</TextField>
	)
}

const RepeatEndDateField = ({
	frequency,
	timezone,
	startScheduleAt,
	value,
	onChange,
}: {
	frequency: LightningReportScheduleFrequency
	timezone: string
	startScheduleAt: string
	value: string | undefined
	onChange: (next: string | undefined) => void
}) => {
	const formatDate = useFormatDate()
	// `startScheduleAt` is a UTC-midnight ISO; treat it as a calendar date independent of the user's timezone.
	const start = startScheduleAt ? dayjs.utc(startScheduleAt) : currentPeriodStart(frequency, timezone, undefined)

	if (frequency === 'DAILY') {
		return <DailyEndDateField minDate={addPeriod(start, frequency, 1)} value={value} onChange={onChange} />
	}

	const PERIODS_TO_SHOW = 50
	const subscriptionEndSentinel = '__subscription_end__'
	const options = periodInstances(addPeriod(start, frequency, 1), frequency, PERIODS_TO_SHOW)
	return (
		<TextField
			data-ta={ids.reports.form.lightning.timeRange.repeatedSchedule.stopScheduleAt}
			select
			fullWidth
			size='small'
			value={value ?? subscriptionEndSentinel}
		>
			<MenuItem value={subscriptionEndSentinel} onClick={() => onChange(undefined)}>
				<I18n i18nKey={($) => $.reports.lightning.common.subscriptionEndDate} />
			</MenuItem>
			{options.map((option) => {
				const iso = option.toISOString()
				return (
					<MenuItem key={iso} value={iso} onClick={() => onChange(iso)}>
						{formatDate(option, 'DD.MM.YYYY.HH.mm', 'UTC')}
					</MenuItem>
				)
			})}
		</TextField>
	)
}

// We want to display "subscription end date" as default placeholder value for Daily to align with Weekly and Monthly.
// However, our DatePicker doesn't support free-form placeholder string, so we render a regular TextField
// that opens a `DateCalendar` in a Popover. A bit hacky but works.
const DailyEndDateField = ({
	minDate,
	value,
	onChange,
}: {
	minDate: Dayjs
	value: string | undefined
	onChange: (next: string | undefined) => void
}) => {
	const formatDate = useFormatDate()
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
	const subscriptionEndLabel = i18next.t(($) => $.reports.lightning.common.subscriptionEndDate)
	const displayValue = value ? formatDate(value, 'DD.MM.YYYY.HH.mm', 'UTC') : subscriptionEndLabel

	return (
		<>
			<TextField
				data-ta={ids.reports.form.lightning.timeRange.repeatedSchedule.stopScheduleAt}
				size='small'
				fullWidth
				value={displayValue}
				onClick={(event) => setAnchorEl(event.currentTarget)}
				slotProps={{
					htmlInput: { readOnly: true, style: { cursor: 'pointer' } },
					input: {
						endAdornment: (
							<InputAdornment position='end'>
								{value ? (
									<MuiIconButton
										size='small'
										onClick={(event) => {
											event.stopPropagation()
											onChange(undefined)
										}}
									>
										<Icon name='Close' size='xsmall' />
									</MuiIconButton>
								) : (
									<Icon name='Calendar' color='textSecondary' size='small' />
								)}
							</InputAdornment>
						),
					},
				}}
			/>
			<Popover
				open={!!anchorEl}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<DateCalendar
						value={value ? dayjs.utc(value) : null}
						timezone='UTC'
						minDate={minDate}
						onChange={(newValue) => {
							if (newValue?.isValid()) {
								onChange(newValue.startOf('day').toISOString())
								setAnchorEl(null)
							}
						}}
					/>
				</LocalizationProvider>
			</Popover>
		</>
	)
}

const RepeatedScheduleInfoAlert = ({
	frequency,
	startScheduleAt,
}: {
	frequency: LightningReportScheduleFrequency
	startScheduleAt: string
}) => {
	const formatDate = useFormatDate()
	const [dataset] = useLightningFormValues(['dataset'])
	// Schedule dates are timezone-independent in the UI — display them in UTC.
	const firstStart = dayjs.utc(startScheduleAt)
	const firstEnd = addPeriod(firstStart, frequency)
	const firstAvailable = firstEnd.add(1, 'minute')
	const dateTimeFormat = 'DD.MM.YYYY.HH.mm' as const

	return (
		<Alert severity='info'>
			<Box>
				<I18n i18nKey={($) => $.reports.lightning.form.repeatedSchedule.info.durationLine[frequency]} />
				<br />
				{dataset === 'NLDN' ? (
					<I18n
						i18nKey={($) => $.reports.lightning.form.repeatedSchedule.info.firstReportNldn}
						values={{
							firstStart: formatDate(firstStart, dateTimeFormat, 'UTC'),
							firstEnd: formatDate(firstEnd, dateTimeFormat, 'UTC'),
						}}
					/>
				) : (
					<I18n
						i18nKey={($) => $.reports.lightning.form.repeatedSchedule.info.firstReport}
						values={{
							firstStart: formatDate(firstStart, dateTimeFormat, 'UTC'),
							firstEnd: formatDate(firstEnd, dateTimeFormat, 'UTC'),
							firstAvailable: formatDate(firstAvailable, dateTimeFormat, 'UTC'),
						}}
					/>
				)}
			</Box>
		</Alert>
	)
}

const StartEndTimeRangeSection = () => {
	const { trigger, setValue } = useFormContext<LightningReportForm>()
	const [dataset, timezone] = useLightningFormValues(['dataset', 'preferences.timezone'])
	const { data: dataAvailability } = useLightningReportDataAvailability({ dataset })

	return (
		<>
			<TimeRangeQuickSelect
				disabled={!dataAvailability}
				maxDate={dayjs(dataAvailability?.end)}
				timezone={timezone}
				onTimeSelected={(start, end) => {
					setValue('timing.time.start', start.toISOString(), { shouldValidate: true })
					setValue('timing.time.end', end.toISOString(), { shouldValidate: true })
					void trigger('timing')
				}}
			/>
			<StartEndDateTimeFields<LightningReportForm>
				names={{ start: 'timing.time.start', end: 'timing.time.end', errorPath: 'timing.time', revalidatePath: 'timing' }}
				timezone={timezone}
				min={dayjs(dataAvailability?.start)}
				disabled={!dataAvailability}
			/>
		</>
	)
}

const FaultTimeTimeRangeSection = () => {
	const { control, setValue } = useFormContext<LightningReportForm>()
	const [dataset, faultTime, buffer, timezone] = useLightningFormValues([
		'dataset',
		'timing.time.faultTime',
		'timing.time.buffer',
		'preferences.timezone',
	])
	const { data: dataAvailability } = useLightningReportDataAvailability({ dataset })

	const onChange = ({ faultTime, buffer }: FaultTime) => {
		setValue('timing.time.faultTime', faultTime)
		setValue('timing.time.buffer', buffer)
	}

	return (
		<>
			<Box>
				<FormLabel required>
					<I18n i18nKey={($) => $.reports.lightning.form.faultDateTime} />
				</FormLabel>
				<Stack data-ta={ids.reports.form.lightning.timeRange.faultTimeEntry.dateTimeField} align='flex-start' flexWrap='wrap'>
					<Controller
						control={control}
						name='timing.time.faultTime'
						render={({ field, fieldState }) => (
							<DateTimeField
								min={dayjs(dataAvailability?.start)}
								max={dayjs(dataAvailability?.end)}
								onChange={(value) => onChange({ faultTime: value.toISOString(), buffer })}
								timezone={timezone}
								value={field.value ? dayjs(field.value) : null}
								disabled={!dataAvailability}
								error={fieldState.error?.message}
							/>
						)}
					/>

					<Stack align='center'>
						<Icon
							data-ta={ids.reports.form.lightning.timeRange.faultTimeEntry.plusMinusIcon}
							name='PlusMinus'
							color='textSecondary'
							size='xsmall'
						/>
						<TextField
							data-ta={ids.reports.form.lightning.timeRange.faultTimeEntry.buffer.select}
							size='small'
							select
							value={buffer.amount}
						>
							{[60, 36, 30, 24, 12, 10, 5, 2, 1, 0.5].map((value) => (
								<MenuItem
									data-ta={ids.reports.form.lightning.timeRange.faultTimeEntry.buffer.amount(value)}
									key={value}
									value={value}
									onClick={() => onChange({ faultTime, buffer: { amount: value, unit: buffer.unit } })}
								>
									{value}
								</MenuItem>
							))}
						</TextField>
						<ToggleButtonGroup exclusive size='small' value={buffer.unit}>
							{(['second', 'minute', 'hour'] as const).map((unit) => (
								<ToggleButton
									data-ta={ids.reports.form.lightning.timeRange.faultTimeEntry.buffer.unit(unit)}
									key={unit}
									value={unit}
									onClick={() => onChange({ faultTime, buffer: { amount: buffer.amount, unit } })}
								>
									<I18n i18nKey={($) => $.reports.lightning.form.faultTimeUnit[unit]} />
								</ToggleButton>
							))}
						</ToggleButtonGroup>
					</Stack>
				</Stack>
			</Box>
			<Controller
				control={control}
				name='timing.time'
				render={({ fieldState }) => <>{fieldState.error?.message && <Alert severity='error'>{fieldState.error?.message}</Alert>}</>}
			/>
		</>
	)
}

export type DurationPreset = { amount: number; unit: 'minute' | 'hour' | 'day' }

// A day keeps the clock time in `timezone`, so it is 23 or 25 hours long across a DST change.
const subtractDuration = (end: Dayjs, { amount, unit }: DurationPreset, timezone: string): Dayjs =>
	unit === 'day' ? end.tz(timezone).subtract(amount, 'day').tz(timezone, true) : end.subtract(amount, unit)

const TimeRangeQuickSelect = ({
	onTimeSelected,
	maxDate,
	timezone,
	disabled,
}: {
	onTimeSelected: (start: Dayjs, end: Dayjs) => void
	maxDate: Dayjs
	timezone: string
	disabled: boolean
}) => {
	const { openMenu, closeMenu, menuProps } = useMenu()
	const explicitButtonItems: DurationPreset[] = [
		{ amount: 1, unit: 'minute' },
		{ amount: 5, unit: 'minute' },
		{ amount: 12, unit: 'hour' },
		{ amount: 24, unit: 'hour' },
	]

	const menuItems: DurationPreset[] = [
		{ amount: 10, unit: 'minute' },
		{ amount: 30, unit: 'minute' },
		{ amount: 1, unit: 'hour' },
		{ amount: 2, unit: 'hour' },
		{ amount: 4, unit: 'hour' },
		{ amount: 6, unit: 'hour' },
		{ amount: 8, unit: 'hour' },
		{ amount: 2, unit: 'day' },
		{ amount: 7, unit: 'day' },
		{ amount: 14, unit: 'day' },
		{ amount: 31, unit: 'day' },
	]

	const formatDuration = ({ amount, unit }: DurationPreset) => {
		switch (unit) {
			case 'minute':
				return <I18n i18nKey={($) => $.reports.lightning.form.durations.min} count={amount} />
			case 'hour':
				return <I18n i18nKey={($) => $.reports.lightning.form.durations.hours} count={amount} />
			case 'day':
				return <I18n i18nKey={($) => $.reports.lightning.form.durations.days} count={amount} />
			default:
				unit satisfies never
		}
	}

	return (
		<Stack direction='row' justifyContent='space-between'>
			{explicitButtonItems.map((duration) => (
				<Button
					data-ta={ids.reports.form.lightning.timeRange.duration(duration)}
					key={`${duration.amount}${duration.unit}`}
					variant='text'
					size='small'
					disabled={disabled}
					onClick={() => onTimeSelected(subtractDuration(maxDate, duration, timezone), maxDate)}
				>
					{formatDuration(duration)}
				</Button>
			))}

			<Button
				data-ta={ids.reports.form.lightning.timeRange.moreBtn}
				variant='text'
				disabled={disabled}
				onClick={openMenu}
				endIcon={<Icon name='EllipsisV' />}
				size='small'
			>
				<I18n i18nKey={($) => $.reports.lightning.form.more} />
			</Button>
			<Menu {...menuProps} title='More'>
				{menuItems.map((duration) => (
					<MenuItem
						data-ta={ids.reports.form.lightning.timeRange.duration(duration)}
						key={`${duration.amount}${duration.unit}`}
						disabled={disabled}
						onClick={() => {
							onTimeSelected(subtractDuration(maxDate, duration, timezone), maxDate)
							closeMenu()
						}}
					>
						{formatDuration(duration)}
					</MenuItem>
				))}
			</Menu>
		</Stack>
	)
}

const DataAvailabilityAlert = () => {
	const [dataset, timezone, timing] = useLightningFormValues(['dataset', 'preferences.timezone', 'timing'])
	const formatDate = useFormatDate()
	const { data: dataAvailability, isError: isErrorLoadingDataAvailability } = useLightningReportDataAvailability({ dataset })

	if (isErrorLoadingDataAvailability) {
		return (
			<Alert severity='error'>
				<I18n i18nKey={($) => $.reports.form.errorLoadingDataAvailability} />
			</Alert>
		)
	}
	if (!dataAvailability) {
		return (
			<Alert severity='info'>
				<I18n i18nKey={($) => $.reports.form.loadingDataAvailability} />
			</Alert>
		)
	}

	const isFaultTimeEntryMode = timing.type === 'ONE_OFF' && timing.time.type === 'fault-time'
	const endTime = timing.type === 'ONE_OFF' ? oneOffTimingToTimeRange(timing).end : undefined
	const isFutureOneOffReport = endTime && dayjs(endTime).isAfter(dataAvailability.end)
	if (isFutureOneOffReport) {
		return (
			<Alert severity='info'>
				<Box>
					<I18n
						i18nKey={($) => $.reports.lightning.form.futureOneOffSchedule}
						values={{ endDateTime: formatDate(endTime, 'DD.MM.YYYY.HH.mm.ss', timezone) }}
					/>
					<br />
					<br />
					<I18n
						i18nKey={($) => $.reports.lightning.form.futureOneOffScheduleAvailability}
						values={{
							startDateTime: formatDate(dataAvailability.start, 'DD.MM.YYYY.HH.mm.ss', timezone),
							endDateTime: formatDate(dataAvailability.end, 'DD.MM.YYYY.HH.mm.ss', timezone),
						}}
					/>
				</Box>
			</Alert>
		)
	}

	return (
		<Alert severity='info'>
			<I18n
				i18nKey={($) => (!isFaultTimeEntryMode ? $.reports.lightning.form.dataAvailabilityWithFutureInfo : $.reports.form.dataAvailability)}
				values={{
					startDateTime: formatDate(dataAvailability.start, 'DD.MM.YYYY.HH.mm.ss', timezone),
					endDateTime: formatDate(dataAvailability.end, 'DD.MM.YYYY.HH.mm.ss', timezone),
				}}
			/>
		</Alert>
	)
}
